import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { fetchOfferApi } from '@/services/offer-api'
import { fetchTrackingConfig, initPixels, trackEvent, trackUmami } from '@/lib/tracking'
import { PhoneInput } from '@/components/ui/phone-input'
import 'react-phone-number-input/style.css'
import { identifyOfferPhone as identifyPhone, rememberedOfferPhone as rememberedPhone, rememberOfferPhone as rememberPhone } from '@/lib/oferta'
import { expiresLabel, formatBRL, OFFER_API, offerTrackingParams, type OfferContext, type OfferInterest } from '@/lib/oferta'
import { OfertaCheckout } from './OfertaCheckout'
import { OfferMessage, OfferSpinner } from './OfferStatus'

type Step = 'offer' | 'reasons' | 'interested' | 'thanks'

function OfertaShell({ context, children }: { context?: OfferContext | null; children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 sm:py-12">
      <img src="/assets/images/slac-logo.svg" alt="SLAC Educação" className="h-8 w-auto mb-8" />
      <main className="w-full max-w-md rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 p-6 sm:p-8">
        {context?.class_name && <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-3">{context.class_name}</p>}
        {children}
      </main>
    </div>
  )
}

/**
 * Oferta de uma etapa do funil (/oferta/:offerId). Tela 1: a oferta e "tenho /
 * não tenho interesse"; quem tem vai para o pagamento, quem não tem diz o
 * porquê na tela 2. Pelo QR, o WhatsApp identifica a pessoa (o mesmo da
 * chamada, que o aparelho já lembra); pelo link pessoal (`?t=`), não pede.
 */
export function OfertaPage() {
  const { offerId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('t')
  const [context, setContext] = useState<OfferContext | null>(null)
  const [step, setStep] = useState<Step>('offer')
  const [result, setResult] = useState<OfferContext | null>(null)
  const [phone, setPhone] = useState(rememberedPhone)
  const [reasons, setReasons] = useState<string[]>([])
  const [otherReason, setOtherReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState<OfferInterest | 'reasons' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'Oferta · SLAC'
    fetchTrackingConfig().then(initPixels).catch(() => {})
    fetchOfferApi(OFFER_API.offer(offerId, token), { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        const body = await response.json().catch(() => null)
        const data = (body?.data ?? { status: 'invalid' }) as OfferContext
        setContext(data)
        if (data.status === 'ok') {
          document.title = `${data.title} · SLAC`
          trackEvent('ViewContent', offerTrackingParams(data))
        }
      })
      .catch(() => setError('Sem conexão. Confira a internet e tente de novo.'))
      .finally(() => setLoading(false))
  }, [offerId, token])

  const send = async (interest: OfferInterest, extra: Record<string, unknown> = {}): Promise<OfferContext | null> => {
    setError(null)
    const identifier = token ? null : identifyPhone(phone)
    if (!token && !identifier) {
      setError('Digite o WhatsApp com DDD.')
      return null
    }

    try {
      const response = await fetchOfferApi(OFFER_API.respond(offerId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...(token ? { t: token } : { phone: identifier }), interest, ...extra }),
      })

      if (response.status === 429) {
        setError('Muitas tentativas. Espere um minuto e tente de novo.')
        return null
      }

      const body = await response.json().catch(() => null)
      if (!response.ok) {
        setError(body?.message ?? 'Não deu certo. Tente de novo.')
        return null
      }

      const data = (body?.data ?? null) as OfferContext | null
      if (data?.status === 'not_found') {
        setError(token
          ? 'Este link não vale mais para você. Fale com a equipe SLAC.'
          : 'Não encontramos esse WhatsApp entre os convidados desta oferta. Confira o número ou fale com a equipe SLAC.')
        return null
      }
      if (data?.status !== 'ok') {
        setContext(data)
        return null
      }
      if (!token) rememberPhone(phone)
      return data
    } catch {
      setError('Sem conexão. Confira a internet e tente de novo.')
      return null
    }
  }

  const choose = async (interest: OfferInterest) => {
    if (!context) return
    setSubmitting(interest)
    const data = await send(interest)
    setSubmitting(null)
    if (!data) return

    setResult(data)

    if (interest === 'not_interested') {
      trackUmami('offer_not_interested', offerTrackingParams(context))
      setStep('reasons')
      return
    }

    trackEvent('Lead', offerTrackingParams(context))
    setStep('interested')
  }

  const submitReasons = async (event: FormEvent) => {
    event.preventDefault()
    if (reasons.length === 0 && !otherReason.trim()) {
      setError('Escolha um motivo ou escreva o seu.')
      return
    }
    setSubmitting('reasons')
    const data = await send('not_interested', { reasons, other_reason: otherReason.trim() || null })
    setSubmitting(null)
    if (data) setStep('thanks')
  }

  const toggleReason = (reason: string) => {
    setReasons((current) => (current.includes(reason) ? current.filter((item) => item !== reason) : [...current, reason]))
  }

  if (loading) {
    return <OfertaShell><OfferSpinner /></OfertaShell>
  }

  if (step === 'interested' && result) {
    // Oferta que vende: o pagamento é aqui mesmo, amarrado a quem respondeu.
    if (result.payment) {
      return (
        <OfertaShell context={context}>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{result.first_name ? `Boa, ${result.first_name}!` : 'Boa!'}</h1>
          <OfertaCheckout
            offerId={offerId}
            token={token}
            phone={token ? null : identifyPhone(phone)}
            context={{ ...result, payment: result.payment }}
          />
        </OfertaShell>
      )
    }

    return (
      <OfertaShell context={context}>
        <OfferMessage
          emoji="🙌"
          title={result.first_name ? `Anotado, ${result.first_name}!` : 'Anotado!'}
          text="A equipe SLAC vai falar com você para garantir a sua vaga."
        />
      </OfertaShell>
    )
  }

  if (step === 'thanks') {
    return (
      <OfertaShell context={context}>
        <OfferMessage
          emoji="💙"
          title={result?.first_name ? `Obrigado, ${result.first_name}!` : 'Obrigado!'}
          text="Agora a gente sabe e não vai ficar te oferecendo o que você não quer."
        />
        <button type="button" onClick={() => choose('interested')} className="w-full text-center text-sm text-blue-600 underline underline-offset-4">
          Mudei de ideia, tenho interesse
        </button>
      </OfertaShell>
    )
  }

  if (step === 'reasons' && context) {
    return (
      <OfertaShell context={context}>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Valeu por avisar!</h1>
        <p className="text-gray-600 mb-6">O que pesou para não ser agora? Pode marcar mais de um.</p>
        <form onSubmit={submitReasons} className="flex flex-col gap-3">
          {(context.objections ?? []).map((reason) => {
            const checked = reasons.includes(reason)
            return (
              <label
                key={reason}
                className={`flex items-center gap-3 rounded-xl border p-4 text-left cursor-pointer transition ${checked ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <input type="checkbox" className="h-5 w-5 accent-blue-600" checked={checked} onChange={() => toggleReason(reason)} />
                <span className="text-gray-900">{reason}</span>
              </label>
            )
          })}
          <label htmlFor="oferta-outro" className="text-sm font-medium text-gray-700 mt-2">Outro motivo</label>
          <textarea
            id="oferta-outro"
            rows={3}
            maxLength={1000}
            value={otherReason}
            onChange={(event) => setOtherReason(event.target.value)}
            className="rounded-xl border border-gray-200 p-3 text-gray-900 focus:border-blue-600 focus:outline-none"
            placeholder="Conta com as suas palavras (opcional)"
          />
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <Button type="submit" size="lg" className="h-12 bg-blue-600 hover:bg-blue-700 text-white text-base" disabled={submitting !== null}>
            {submitting === 'reasons' ? 'Enviando…' : 'Enviar'}
          </Button>
          <button type="button" onClick={() => setStep('thanks')} className="text-sm text-gray-500 underline underline-offset-4">
            Prefiro não dizer
          </button>
        </form>
      </OfertaShell>
    )
  }

  const status = context?.status

  if (status === 'expired') {
    return (
      <OfertaShell context={context}>
        <OfferMessage emoji="⏱️" title="Esta oferta já expirou" text="Fale com a equipe SLAC se ainda tiver interesse." />
      </OfertaShell>
    )
  }

  if (status === 'closed') {
    return (
      <OfertaShell context={context}>
        <OfferMessage emoji="🔒" title="Esta oferta foi encerrada" text="Fale com a equipe SLAC se ainda tiver interesse." />
      </OfertaShell>
    )
  }

  if (status === 'not_found') {
    return (
      <OfertaShell context={context}>
        <OfferMessage emoji="🔎" title="Este link não vale para você" text="Fale com a equipe SLAC se tiver interesse na oferta." />
      </OfertaShell>
    )
  }

  if (status !== 'ok' || !context) {
    return (
      <OfertaShell>
        <OfferMessage emoji="🔎" title={error ? 'Sem conexão' : 'QR inválido'} text={error ?? 'Aponte a câmera para o QR projetado na sala ou procure a equipe SLAC.'} />
      </OfertaShell>
    )
  }

  const validity = expiresLabel(context.expires_at)

  return (
    <OfertaShell context={context}>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{context.title}</h1>
      {context.payment && (
        <p className="text-xl font-semibold text-blue-700 mb-2 tabular-nums">
          {context.payment.label}
          {context.payment.original_price && (
            <span className="ml-2 text-base font-normal text-gray-400 line-through">{formatBRL(context.payment.original_price)}</span>
          )}
          {context.payment.installments > 1 && (
            <span className="block text-base font-normal text-gray-600">ou {formatBRL(context.payment.price)} à vista</span>
          )}
        </p>
      )}
      {context.description && <p className="text-gray-600 whitespace-pre-line mb-2">{context.description}</p>}
      {validity && <p className="text-sm text-gray-500 mb-6">{validity}</p>}
      {context.identified && context.first_name && <p className="text-gray-700 mb-6">Oi, {context.first_name}! Conta pra gente:</p>}

      {!context.identified && (
        <div className="flex flex-col gap-2 mb-6">
          <label htmlFor="oferta-phone" className="text-sm font-medium text-gray-700">Seu WhatsApp</label>
          <div className="phone-input-container">
            <PhoneInput
              defaultCountry="BR"
              value={phone}
              onChange={(value) => setPhone(value ?? '')}
              id="oferta-phone"
              name="phone"
              autoComplete="tel"
              placeholder="(11) 98765-4321"
              countries={['BR', 'US', 'AR', 'CL', 'CO', 'PE', 'MX', 'UY', 'PY', 'BO']}
            />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mb-4" role="alert">{error}</p>}

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          size="lg"
          className="h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg"
          disabled={submitting !== null}
          onClick={() => choose('interested')}
        >
          {submitting === 'interested' ? 'Um instante…' : 'Tenho interesse'}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="h-14 text-lg"
          disabled={submitting !== null}
          onClick={() => choose('not_interested')}
        >
          {submitting === 'not_interested' ? 'Um instante…' : 'Não tenho interesse'}
        </Button>
      </div>
    </OfertaShell>
  )
}
